import type { BoxProps, SxProps, Theme } from '@mui/material';
import { Box } from '@packages/shared';
import { useIntersectionObserver } from '@packages/shared/src/hooks/useIntersectionObserver/useIntersectionObserver';
import { useEffect, useRef } from 'react';

const StickyInsideParentWrapper: SxProps<Theme> = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
};

const StickyOutsideViewportWrapper: SxProps<Theme> = {
  display: 'block',
  width: '100%',
  position: 'fixed',
  zIndex: 1,
  bottom: 0,
  left: 0,
};

export interface StickyProps extends BoxProps {
  children: React.ReactNode;
}

export const Sticky: React.FC<StickyProps> = ({ children, ...props }) => {
  const stickyContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollTopButton = document.getElementById('scrollup-wrapper');

    if (scrollTopButton) {
      if (stickyContainerRef && stickyContainerRef.current) {
        scrollTopButton.style.transform = `translateY(-${stickyContainerRef.current.clientHeight}px)`;
      }
    }

    return () => {
      if (scrollTopButton) {
        scrollTopButton.style.transform = 'translateY(0)';
      }
    };
  }, []);

  return (
    <Box
      ref={stickyContainerRef}
      {...props}
      // we need overwrite props and include our own sx along with props sx
      sx={{
        ...StickyOutsideViewportWrapper,
        ...props?.sx,
      }}
    >
      {children}
    </Box>
  );
};

export const StickyInsideParent: React.FC<StickyProps> = ({ children, ...props }) => (
  // we need overwrite props and include our own sx along with props sx
  <Box {...props} sx={{ ...StickyInsideParentWrapper, ...(props?.sx ? props.sx : {}) }}>
    {children}
  </Box>
);

export const StickyOutsideViewport: React.FC<StickyProps> = ({ children, ...props }) => {
  const onSiteElementRef = useRef<HTMLElement>(null);
  const { isIntersecting } = useIntersectionObserver(onSiteElementRef, {
    threshold: 0.1,
    intersectOnce: false,
  });

  return (
    <>
      <Box ref={onSiteElementRef} {...props}>
        {children}
      </Box>
      {!isIntersecting && <Sticky {...props}>{children}</Sticky>}
    </>
  );
};
